<script setup>
import { watch, onMounted, computed, ref, provide, nextTick, defineAsyncComponent } from "vue";
import { RouterView, useRoute, useRouter } from "vue-router";

import { useUserAuthStore } from "@/stores/userAuthStore";
import { useClientCompaniesStore } from "@/stores/clientCompaniesStore";
import { useClientProjectsStore } from "@/stores/clientProjectsStore";
import { useAdminProjectsStore } from "@/stores/adminProjectsStore";
import { useAdminCompaniesStore } from "@/stores/adminCompaniesStore";
import { useClientAssetsStore } from "@/stores/clientAssetsStore";
import { useAdminAssetsStore } from "@/stores/adminAssetsStore";
import { useAdminCalendarStore } from "@/stores/adminCalendarStore";
import { useClientCalendarStore } from "@/stores/clientCalendarStore";
import { useAdminDeliverablesStore } from "@/stores/adminDeliverablesStore";
import { useAdminShowsStore } from "@/stores/adminShowsStore";
import { useClientDeliverablesStore } from "@/stores/clientDeliverablesStore";
import { useClientReviewStore } from "@/stores/clientReviewStore";
import { useNotificationsStore } from "@/stores/notificationsStore";

import NavBar from "@/components/global/navBar.vue";
import LoadingSpinner from "@/components/global/LoadingSpinner.vue";
import ToastProvider from "@/components/global/ToastProvider.vue";
import MinimizedDeliverablesToolbar from "@/components/admin/deliverable/MinimizedDeliverablesToolbar.vue";
import DeliverableModal from "@/components/admin/deliverable/DeliverableModal.vue";

const ConversationWidget = defineAsyncComponent(() =>
  import("@/components/client/conversations/ConversationWidget.vue")
);
import { useConversationsStore } from "@/stores/conversationsStore";
import { useClientPermissions } from "@/composables/useClientPermissions";
import { useStoreSelector } from "@/composables/useStoreSelector";
import { isAdminUser } from "@/utils/userUtils";
import { useViewModeStore } from "@/stores/viewModeStore";
import { openCompanySwitcherGlobal } from "@/composables/useNavbarState";
import { useGlobalLoadingStore } from "@/stores/globalLoadingStore";
import { useImpersonationService } from "@/services/impersonationService";
import { logger } from "@/services/loggerService";
import { companyService } from '@/services/companyService';
import { useAppLoading, isAppLoading } from '@/composables/useAppLoading';
import { companyCache } from "@/services/companyCache";


/**
 * App.vue - Application Root Component
 *
 * This component serves as the entry point for the application and handles:
 * 1. Authentication state management
 * 2. Initial data loading based on user type
 * 3. Store initialization and coordination
 * 4. Global reactive properties provided to child components
 * 5. Route-based data loading
 * 6. Early theme caching and application
 */

const userAuthStore = useUserAuthStore();
const clientCompaniesStore = useClientCompaniesStore();
const clientProjectsStore = useClientProjectsStore();
const adminProjectsStore = useAdminProjectsStore();
const adminCompaniesStore = useAdminCompaniesStore();
const clientAssetsStore = useClientAssetsStore();
const adminAssetsStore = useAdminAssetsStore();
const adminCalendarStore = useAdminCalendarStore();
const clientCalendarStore = useClientCalendarStore();
const adminDeliverablesStore = useAdminDeliverablesStore();
const adminShowsStore = useAdminShowsStore();
const clientDeliverablesStore = useClientDeliverablesStore();
const clientReviewStore = useClientReviewStore();
const notificationsStore = useNotificationsStore();
const route = useRoute();
const router = useRouter();

const { activeStores } = useStoreSelector();
const viewModeStore = useViewModeStore();
const globalLoadingStore = useGlobalLoadingStore();
const { handleRouteChange, isTransitioning, initializeService, stopImpersonation } =
  useImpersonationService();

// Handle banner exit — stop impersonation and navigate to Nexus
const handleBannerExit = async () => {
  try {
    // stopImpersonation() navigates to the company we were viewing.
    await stopImpersonation();
  } catch (error) {
    logger.error('[App] Banner exit failed, forcing navigation', { error: error.message });
    router.push({ name: 'admin-companies' });
  }
};

// Handle navbar mounted event
const handleNavbarMounted = () => {
  logger.debug("[App] NavBar component mounted");
};

// Handle restoring minimized deliverable
const handleRestoreDeliverable = async (deliverable) => {
  logger.info('[App] Restoring minimized deliverable', {
    deliverableId: deliverable.id,
    title: deliverable.title,
    currentRoute: route.path
  });

  // Ensure stores are initialized
  if (!adminDeliverablesStore.initialized) {
    await adminDeliverablesStore.initialize();
  }
  if (!adminProjectsStore.initialized) {
    await adminProjectsStore.init();
  }
  if (!adminCompaniesStore.initialized) {
    await adminCompaniesStore.getAllCompanies();
  }

  // Fetch the full deliverable data
  const fullDeliverable = adminDeliverablesStore.getDeliverableById(deliverable.id);

  if (fullDeliverable || deliverable) {
    // Use full data if available, otherwise use what we have
    const deliverableToOpen = fullDeliverable || deliverable;

    // Fetch project and company data if we don't have them
    let project = deliverable.project;
    let company = deliverable.company;

    if (!project && (deliverable.projectId || deliverableToOpen.projectId)) {
      const projectId = deliverable.projectId || deliverableToOpen.projectId;
      project = adminProjectsStore.getProjectById(projectId);
    }

    if (!company && (deliverable.companyId || deliverableToOpen.companyId)) {
      const companyId = deliverable.companyId || deliverableToOpen.companyId;
      company = adminCompaniesStore.getCompanyById(companyId);
    }

    // Open the modal using the store
    adminDeliverablesStore.openDeliverableModal(
      deliverableToOpen,
      project,
      company
    );

    // Remove from minimized list
    adminDeliverablesStore.restoreDeliverable(deliverable.id);
    // URL is updated by openDeliverableModal in the store
  } else {
    logger.warn('[App] Could not find deliverable data', {
      deliverableId: deliverable.id
    });
  }
};

// Handle closing the deliverable modal
const handleDeliverableModalClose = (val) => {
  // Only handle boolean values for open/close state
  // Ignore non-boolean values from @saved/@deleted events which pass data payloads
  if (typeof val !== 'boolean') {
    return;
  }

  // Don't set isOpen = true directly - that should only happen via openDeliverableModal
  // which properly sets all the modal data (deliverable, project, company)
  if (!val) {
    // Close the modal store state (URL is updated by closeDeliverableModal)
    adminDeliverablesStore.closeDeliverableModal();
  }
};

// SECTION: Store Selection Logic
/**
 * Dynamically selects the appropriate company store based on user type.
 * Admin/super users use the admin store, regular users use the client store.
 */
const getCompanyStore = computed(() => activeStores.value.companyStore);

// Open (or close) the deliverable modal from the ?deliverable= query param.
//
// A shared deep link races THREE independent async readinesses on a cold load:
//   1. the router resolving the initial navigation to /nexus/...
//   2. Firebase auth restoring the signed-in user
//   3. the admin stores initializing
// The previous implementation keyed only on `route.query.deliverable`, so if the
// route wasn't /nexus/ yet (or the user wasn't authed yet) when it first ran, it
// bailed silently — and since the query param never changed afterward, it never
// re-fired. That's why deep links opened "sometimes" but not always.
//
// Keying on the COMBINATION of [param, path, user] makes the handler re-run the
// moment any of those readinesses resolves, so whichever wins the race last
// still triggers the open. No pending flag, no setTimeout re-trigger dance.
watch(
  () => [route.query.deliverable, route.path, userAuthStore.user?.uid],
  async ([deliverableId, path]) => {
    // Param removed → close the modal if we have it open.
    if (!deliverableId) {
      if (adminDeliverablesStore.modalState.isOpen) {
        adminDeliverablesStore.closeDeliverableModal();
      }
      return;
    }

    // The modal is admin-only and lives under /nexus/. If either isn't ready
    // yet, bail WITHOUT giving up — a later route/auth change re-runs this.
    if (!path.startsWith('/nexus/')) return;
    if (!isAdminUser(userAuthStore.user)) return;

    // Already open with this deliverable — nothing to do.
    if (adminDeliverablesStore.modalState.isOpen &&
        adminDeliverablesStore.modalState.deliverable?.id === deliverableId) {
      return;
    }

    logger.info('[App] Opening deliverable from URL query', {
      deliverableId,
      currentRoute: path
    });

    // Make sure all required stores are initialized.
    await nextTick();
    if (!adminDeliverablesStore.initialized) {
      await adminDeliverablesStore.initialize();
    }
    if (!adminProjectsStore.initialized) {
      await adminProjectsStore.init();
    }
    if (!adminCompaniesStore.companies || adminCompaniesStore.companies.length === 0) {
      await adminCompaniesStore.getAllCompanies();
    }

    // Wait for deliverables to actually load (initialize() sets up a listener).
    let retries = 0;
    const maxRetries = 10;
    let deliverable = null;
    while (retries < maxRetries) {
      deliverable = adminDeliverablesStore.getDeliverableById(deliverableId);
      if (deliverable) break;
      await new Promise(resolve => setTimeout(resolve, 200));
      retries++;
    }

    if (!deliverable) {
      logger.warn('[App] Could not find deliverable from URL query after waiting', {
        deliverableId,
        retriesAttempted: retries
      });
      return;
    }

    // Find project and company. Episode and archived projects are EXCLUDED from
    // adminProjectsStore.init() (it filters out type==='episode' and
    // status==ARCHIVED), so a deep link to a deliverable on one of those finds
    // nothing in `items`. Fall back to a direct fetch — this also covers
    // projects/companies that simply haven't finished loading yet.
    let project = adminProjectsStore.items[deliverable.projectId];
    if (!project && deliverable.projectId) {
      project = await adminProjectsStore.fetchProjectById(deliverable.projectId);
    }
    let company = adminCompaniesStore.getCompanyById(deliverable.companyId);
    if (!company && deliverable.companyId) {
      company = await adminCompaniesStore.getCompany(deliverable.companyId);
    }

    if (project && company) {
      adminDeliverablesStore.openDeliverableModal(deliverable, project, company);
    } else {
      logger.warn('[App] Could not find project or company for deliverable', {
        deliverableId,
        projectId: deliverable.projectId,
        companyId: deliverable.companyId,
        projectFound: !!project,
        companyFound: !!company
      });
    }
  },
  { immediate: true }
);

// SECTION: Lifecycle Hooks
/**
 * On component mount:
 * 1. Initialize authentication if needed
 * 2. Load appropriate store data based on user type
 */
onMounted(async () => {
  logger.info("[App] Starting application initialization");

  // Check if we need to highlight an element from feedback
  checkForElementHighlight();

  // Apply cached theme immediately to prevent flash of default styling
  const userCompanyId = userAuthStore.user?.companyId;
  if (userCompanyId) {
    const cachedTheme = companyCache.getCachedTheme(userCompanyId);
    if (cachedTheme) {
      logger.info("[App] Applied cached theme early", {
        companyId: userCompanyId,
        companyName: cachedTheme.companyName,
      });
    }
  }

  try {
    // First initialize auth - this must happen before other initialization
    await userAuthStore.init();
    logger.info("[App] Auth initialized", {
      userType: userAuthStore.user?.type,
    });

    // Apply cached theme again after auth loads in case user data changed
    const updatedUserCompanyId = userAuthStore.user?.companyId;
    if (updatedUserCompanyId && updatedUserCompanyId !== userCompanyId) {
      const cachedTheme = companyCache.getCachedTheme(updatedUserCompanyId);
      if (cachedTheme) {
        logger.info("[App] Applied cached theme after auth", {
          companyId: updatedUserCompanyId,
          companyName: cachedTheme.companyName,
        });
      }
    }

    // Always ensure admin companies are loaded for admin/super users, even when impersonating
    // This must happen BEFORE impersonation check so navbar has company data
    if (
      userAuthStore.user?.type === "admin" ||
      userAuthStore.user?.type === "super"
    ) {
      if (!adminCompaniesStore.initialized) {
        logger.info("[App] Initializing admin companies store for admin user");
        await adminCompaniesStore.init();
      }
    }

    // Check if we're in an impersonation state
    if (viewModeStore.isImpersonating && viewModeStore.impersonatedCompany) {
      // Check if we're on an admin route while impersonating - this shouldn't happen
      if (route.path.startsWith("/nexus/")) {
        logger.warn(
          "[App] Detected impersonation state on admin route, cleaning up",
          {
            route: route.path,
            impersonatedCompany: viewModeStore.impersonatedCompany,
          }
        );

        // Clean up impersonation state
        const impersonationService = useImpersonationService(router);
        await impersonationService.resetClientStores();
        viewModeStore.stopImpersonation();
        impersonationService.updateTheme(null);

        logger.info("[App] Impersonation cleanup completed");
      } else {
        logger.info("[App] Loading impersonated company data", {
          companyId: viewModeStore.impersonatedCompany,
        });
        // Initialize impersonation service only when needed
        await initializeService();
        // Load impersonated company data
        await Promise.all([
          activeStores.value.companyStore.getCompany(
            viewModeStore.impersonatedCompany
          ),
          activeStores.value.projectStore.getCompanyProjects(
            viewModeStore.impersonatedCompany
          ),
          clientAssetsStore.init(viewModeStore.impersonatedCompany),
          clientCalendarStore.init(viewModeStore.impersonatedCompany),
          clientDeliverablesStore.init(viewModeStore.impersonatedCompany),
          clientReviewStore.init(viewModeStore.impersonatedCompany),
        ]);
        logger.info("[App] Impersonation data loading complete");
        return; // Skip normal initialization
      }
    }

    // Normal initialization if not impersonating
    if (
      userAuthStore.user?.type === "admin" ||
      userAuthStore.user?.type === "super"
    ) {
      logger.info(
        "App: Admin user detected, initialization will be handled by view context"
      );
    } else if (userAuthStore.user?.type === "user") {
      logger.info("App: Initializing user data");

      // For multi-company users, initialization will happen after company selection
      // For single-company users, initialize with their companyId
      if (userAuthStore.user.companies && userAuthStore.user.companies.length > 1) {
        logger.info("App: Multi-company user detected, waiting for company selection", {
          companyCount: userAuthStore.user.companies.length
        });
      } else if (userAuthStore.user.companyId) {
        await Promise.all([
          clientCompaniesStore.init(userAuthStore.user.id, userAuthStore.user, userAuthStore.user.companyId),
          clientProjectsStore.getCompanyProjects(userAuthStore.user.companyId),
          clientAssetsStore.init(userAuthStore.user.companyId),
          clientCalendarStore.init(userAuthStore.user.companyId),
          clientDeliverablesStore.init(userAuthStore.user.companyId),
          clientReviewStore.init(userAuthStore.user.companyId),
        ]);
        logger.info("App: Single-company user initialization complete");
      } else {
        logger.warn("App: User has no companies assigned", {
          userId: userAuthStore.user.id
        });
      }
    }
  } catch (error) {
    logger.error("[App] Failed to initialize application:", {
      error: error.message,
    });
  }
});

// SECTION: Data Loading Functions
/**
 * Centralizes admin data loading to avoid code duplication.
 * Initializes both project and company stores for admin users.
 * Catches and logs errors without breaking the application flow.
 */
const loadAdminData = async () => {
  logger.info("[App] Loading admin data");
  try {
    // Load all admin data in parallel
    const loadPromises = [];

    if (!adminProjectsStore.initialized) {
      logger.debug("[App] Initializing admin projects store");
      loadPromises.push(adminProjectsStore.init());
    }

    if (!adminCompaniesStore.initialized) {
      logger.debug("[App] Initializing admin companies store");
      loadPromises.push(adminCompaniesStore.init());
    }

    if (!adminAssetsStore.initialized) {
      logger.debug("[App] Initializing admin assets store");
      loadPromises.push(adminAssetsStore.init());
    }

    if (!adminCalendarStore.initialized) {
      logger.debug("[App] Initializing admin calendar store");
      loadPromises.push(adminCalendarStore.init());
    }

    if (!adminDeliverablesStore.initialized) {
      logger.debug("[App] Initializing admin deliverables store");
      loadPromises.push(adminDeliverablesStore.initialize());
    }

    if (!adminShowsStore.initialized) {
      logger.debug("[App] Initializing admin shows store");
      loadPromises.push(adminShowsStore.init());
    }

    // Always try to load users if we're an admin
    if (userAuthStore.isAdmin) {
      logger.debug("[App] Loading users list");
      loadPromises.push(
        userAuthStore.listAllUsers().catch((error) => {
          logger.error("[App] Failed to load users:", {
            error: error.message,
          });
          return null;
        })
      );
    }

    if (loadPromises.length > 0) {
      await Promise.all(loadPromises);
    }

    logger.info("[App] Admin data loading complete");
  } catch (error) {
    logger.error("[App] Failed to load admin data:", {
      error: error.message,
    });
    throw error;
  }
};

// SECTION: View Context Management
/**
 * Determines the current view context based on impersonation state and user type
 */
const currentViewContext = computed(() => {
  if (viewModeStore.isImpersonating) {
    return {
      mode: "client",
      companyId: viewModeStore.impersonatedCompany,
    };
  }

  return {
    mode:
      userAuthStore.user?.type === "admin" ||
      userAuthStore.user?.type === "super"
        ? "admin"
        : "client",
    companyId: userAuthStore.user?.companyId || null,
  };
});

// Watch for view context changes
watch(
  currentViewContext,
  async (newContext, oldContext) => {
    // For initial load (oldContext is undefined), always proceed if admin mode
    const isInitialLoad = oldContext === undefined;

    // Skip if context hasn't actually changed (but not on initial load)
    if (
      !isInitialLoad &&
      newContext?.mode === oldContext?.mode &&
      newContext?.companyId === oldContext?.companyId
    )
      return;

    logger.info("[App] View context changed to:", newContext, {
      isInitialLoad,
    });
    if (newContext.mode === "admin" && !viewModeStore.isImpersonating) {
      logger.debug("[App] Loading admin data for admin mode");
      await loadAdminData();
    }
  },
  { immediate: true }
);

// SECTION: Auth State Changes
watch(
  () => userAuthStore.user,
  async (newUser, oldUser) => {
    if (!newUser) {
      // User logged out - reset stores
      viewModeStore.reset();
      notificationsStore.reset();
      return;
    }

    // User logged in - initialize notifications store
    if (newUser.id && (!oldUser || oldUser.id !== newUser.id)) {
      logger.info("[App] Initializing notifications store for user", { userId: newUser.id });
      await notificationsStore.initialize(newUser.id);
    }
  }
);

/**
 * Watch route changes to update document title.
 */
watch(
  [() => route.meta.title, () => notificationsStore.unreadCount],
  ([title, unreadCount]) => {
    const baseTitle = title || "Anthem Portal";
    document.title = unreadCount > 0 ? `(${unreadCount}) ${baseTitle}` : baseTitle;
  },
  { immediate: true }
);

const user = computed(() => userAuthStore.user);

// SECTION: Company Tracking
/**
 * Track the current company from route params.
 * This allows different views to access the same company data.
 */
const companyRef = ref(null);

/**
 * Watch company route parameter changes to load company data.
 * This handles different loading strategies based on user type.
 * Note: Client routes use params.id, admin routes use params.company
 */
watch(
  () => route.params.id || route.params.company,
  async (companyId) => {
    logger.debug("[App] Route params watcher triggered", {
      routePath: route.path,
      routeName: route.name,
      paramsId: route.params.id,
      paramsCompany: route.params.company,
      companyId,
      userType: user.value?.type,
    });

    if (!companyId) {
      logger.debug("[App] No company ID found, clearing company ref");
      companyRef.value = null;
      return;
    }

    // If we're impersonating and this is the impersonated company, skip loading
    if (
      viewModeStore.isImpersonating &&
      viewModeStore.impersonatedCompany === companyId
    ) {
      logger.debug("[App] Skipping load - already impersonating this company");
      return;
    }

    const userType = user.value?.type;
    const store = getCompanyStore.value;

    logger.debug("[App] Loading company data", {
      companyId,
      userType,
      storeName: store?.constructor?.name,
    });

    if (userType === "admin" || userType === "super") {
      // For admin users: try to find company in already loaded companies first
      // Get company from all companies in admin store
      const allCompanies = store.companies;
      companyRef.value = allCompanies.find((c) => c.id === companyId) || null;

      // If not found and companies aren't loaded yet, fetch all companies
      // If not found, try to fetch it
      if (!companyRef.value && allCompanies.length === 0) {
        // If the store hasn't loaded companies yet, trigger the load
        await store.getAllCompanies();
        companyRef.value =
          store.companies.find((c) => c.id === companyId) || null;
      }

      // For admin users, also ensure assets are loaded for this company
      if (companyRef.value && adminAssetsStore.initialized) {
        try {
          await adminAssetsStore.loadAssetsForCompany(companyId);
          logger.debug("[App] Admin assets loaded for company", {
            companyId,
            assetCount: adminAssetsStore.getAssetsForCompany(companyId).length,
          });
        } catch (error) {
          logger.warn("[App] Failed to load admin assets for company", {
            companyId,
            error: error.message,
          });
        }
      }
    } else {
      // For regular users: directly fetch the specific company and initialize stores
      try {
        logger.debug("[App] Calling store.getCompany for client user", {
          companyId,
        });
        const result = await store.getCompany(companyId);
        companyRef.value = result;
        
        // Initialize client stores for this company if not already initialized for this company
        const needsStoreInit = !clientProjectsStore.initialized || 
                               clientProjectsStore.currentCompanyId !== companyId;
        
        if (needsStoreInit) {
          logger.info("[App] Initializing client stores for selected company", { companyId });
          await Promise.all([
            clientCompaniesStore.init(userAuthStore.user.id, userAuthStore.user, companyId),
            clientProjectsStore.getCompanyProjects(companyId),
            clientAssetsStore.init(companyId),
            clientCalendarStore.init(companyId),
            clientDeliverablesStore.init(companyId),
            clientReviewStore.init(companyId),
          ]);
          logger.info("[App] Client stores initialized for company", { companyId });
        }
        
        logger.debug("[App] Company data loaded for client", {
          companyId,
          result,
          companyRefValue: companyRef.value,
          companyName: companyRef.value?.name,
          hasLogoUrl: !!companyRef.value?.logoUrl,
          logoUrl: companyRef.value?.logoUrl,
        });
      } catch (error) {
        logger.error("[App] Failed to load company data for client", {
          companyId,
          error: error.message,
        });
        companyRef.value = null;
      }
    }
  },
  { immediate: true }
);

/**
 * Computed property that returns the current company.
 * Falls back to the store's currentCompany if companyRef is null.
 */
const _company = computed(() => {
  return companyRef.value || getCompanyStore.value.currentCompany;
});

// SECTION: Element Highlighting for Feedback
const checkForElementHighlight = () => {
  if (window.location.hash === "#vysual-highlight") {
    const highlightData = localStorage.getItem("vysual_highlight_element");
    if (highlightData) {
      try {
        const data = JSON.parse(highlightData);

        // Only use data if it's recent (within last minute)
        if (Date.now() - data.timestamp < 60000) {
          setTimeout(() => {
            highlightReportedElement(data);
          }, 1500); // Wait for page to fully load
        }

        // Clean up
        localStorage.removeItem("vysual_highlight_element");
        // Remove hash from URL
        history.replaceState(
          null,
          null,
          window.location.pathname + window.location.search
        );
      } catch (e) {
        console.error("Error highlighting element:", e);
      }
    }
  }
};

const highlightReportedElement = (data) => {
  let targetElement = null;

  // Try to find element by ID first
  if (data.id) {
    targetElement = document.getElementById(data.id);
  }

  // Try CSS selector
  if (!targetElement && data.selector) {
    try {
      targetElement = document.querySelector(data.selector);
    } catch {
      console.error("Invalid selector:", data.selector);
    }
  }

  if (targetElement) {
    // Scroll to element
    targetElement.scrollIntoView({ behavior: "smooth", block: "center" });

    // Create highlight overlay
    const highlight = document.createElement("div");
    const rect = targetElement.getBoundingClientRect();
    highlight.style.cssText = `
      position: fixed;
      left: ${rect.left - 3}px;
      top: ${rect.top - 3}px;
      width: ${rect.width + 6}px;
      height: ${rect.height + 6}px;
      border: 3px solid #ff4757;
      background: rgba(255, 71, 87, 0.1);
      z-index: 99999;
      pointer-events: none;
      animation: vysual-pulse 2s infinite;
      border-radius: 4px;
    `;

    // Add pulsing animation
    const style = document.createElement("style");
    style.textContent = `
      @keyframes vysual-pulse {
        0% { 
          box-shadow: 0 0 0 0 rgba(255, 71, 87, 0.7);
          transform: scale(1);
        }
        50% {
          transform: scale(1.02);
        }
        70% { 
          box-shadow: 0 0 0 20px rgba(255, 71, 87, 0);
          transform: scale(1);
        }
        100% { 
          box-shadow: 0 0 0 0 rgba(255, 71, 87, 0);
          transform: scale(1);
        }
      }
    `;
    document.head.appendChild(style);

    // Add label
    const label = document.createElement("div");
    label.style.cssText = `
      position: fixed;
      left: ${rect.left}px;
      top: ${rect.top - 35}px;
      background: #ff4757;
      color: white;
      padding: 6px 12px;
      font-size: 13px;
      border-radius: 4px;
      z-index: 100000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
    `;
    label.innerHTML = `
      <strong>📍 Reported Element</strong><br>
      <small style="opacity: 0.9">${data.tagName}${
      data.id ? "#" + data.id : ""
    } • Click anywhere to dismiss</small>
    `;

    document.body.appendChild(highlight);
    document.body.appendChild(label);

    // Remove highlight after 15 seconds
    const removeHighlight = () => {
      highlight.remove();
      label.remove();
      style.remove();
    };

    setTimeout(removeHighlight, 15000);

    // Also remove on click
    document.addEventListener("click", removeHighlight, { once: true });
  } else {
    // Show a notification that element wasn't found
    const notification = document.createElement("div");
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #ff4757;
      color: white;
      padding: 16px 24px;
      border-radius: 8px;
      z-index: 100000;
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
      max-width: 400px;
    `;
    notification.innerHTML = `
      <strong>⚠️ Element Not Found</strong><br>
      <small>The reported element could not be located on this page. It may have been moved or removed.</small><br>
      <small style="opacity: 0.7">Selector: ${data.selector}</small>
    `;
    document.body.appendChild(notification);

    setTimeout(() => notification.remove(), 5000);
  }
};

// SECTION: Dependency Injection
/**
 * Provide user and company to descendant components.
 * This allows any component to access these values without prop drilling.
 */
provide("user", user);
provide("company", companyRef);
provide("viewMode", viewModeStore);

const isDev = import.meta.env.DEV;

const isLoading = computed(() => userAuthStore.isLoading);
const showMinimizedDeliverablesToolbar = computed(() => {
  // Only show on admin routes and when user is authenticated as admin
  return route.path.startsWith('/nexus/') &&
         userAuthStore.user &&
         isAdminUser(userAuthStore.user);
});

const showNavbar = computed(() => {
  // Don't show navbar for public routes
  if (route.meta.public === true) {
    return false;
  }
  // Don't show navbar on SingleCard layout pages (login, account selector, desktop auth)
  const singleCardRoutes = ["login", "account-selector"];
  if (singleCardRoutes.includes(route.name)) {
    return false;
  }
  return !isLoading.value && userAuthStore.user.id;
});

// Conversations: client users and impersonating admins, on /accounts/*, when
// the company has the feature on and the viewer is above view-only.
const conversationsStore = useConversationsStore();
const { canAccessConversations } = useClientPermissions();
const conversationsCompanyId = computed(() => {
  if (isLoading.value) return null;
  const isClientViewer =
    userAuthStore.user?.type === 'user' || viewModeStore.isImpersonating;
  if (!isClientViewer) return null;
  if (!route.path.startsWith('/accounts/')) return null;
  // `?demo=1` on the Conversations page runs on local sample data only.
  if (route.query.demo === '1') return null;
  if (!canAccessConversations.value) return null;
  return route.params.id || null;
});

watch(conversationsCompanyId, (companyId) => {
  if (companyId) conversationsStore.start(companyId);
  else conversationsStore.stop();
}, { immediate: true });

const _hideFooter = computed(() => {
  // Hide footer on admin-feedback route
  return route.name === "admin-feedback";
});

// Provide as a global computed property for the navBar component
provide("storeSelector", getCompanyStore);

// Add route change watcher
watch(
  () => route.path,
  async (_newPath) => {
    // Clear client stores when navigating to account selector to prevent data crossover
    if (route.name === 'account-selector') {
      logger.info('[App] Clearing client stores for account selector');

      try {
        // Reset client stores
        if (clientCompaniesStore.initialized) {
          clientCompaniesStore.reset();
        }
        if (clientProjectsStore.initialized) {
          clientProjectsStore.reset();
        }
        if (clientAssetsStore.initialized) {
          clientAssetsStore.reset();
        }
        if (clientCalendarStore.initialized) {
          clientCalendarStore.reset();
        }
        if (clientDeliverablesStore.initialized) {
          clientDeliverablesStore.reset();
        }
        if (clientReviewStore.initialized) {
          clientReviewStore.reset();
        }

        // Clear company theme
        updateTheme(null);

        logger.info('[App] Client stores and theme cleared for account selector');
      } catch (error) {
        logger.error('[App] Error clearing stores for account selector:', { error: error.message });
      }
    }

    await handleRouteChange(route);
  }
);

// SECTION: Theme Management
/**
 * Updates theme based on company brand color.
 * Delegates to companyCache as the single source of truth for theme vars.
 */
const updateTheme = (company) => {
  if (!company?.brandColor) {
    companyCache.clearTheme();
    logger.info("[App] Removed theme override");
    return;
  }

  companyCache.setAndApplyTheme(company.id, company);
  logger.info("[App] Applied company theme", {
    companyName: company.name,
    brandColor: company.brandColor,
  });
};

// Computed to check if we're in client context
const isClientContext = computed(() => {
  // Check if user is a client user or if admin is impersonating
  return userAuthStore.user?.type === "user" || viewModeStore.isImpersonating;
});


const isAgencyPortal = computed(() => route.path.startsWith("/agency/"));

// Auth registers with the same loader every page uses, so there is one signal
// and one component — not two conditions racing each other.
const { waitFor } = useAppLoading('auth');
waitFor(() => !isLoading.value);

// The agency's own company, purely so the context bar can name it. Loaded once
// per agency rather than on every route change.
const agencyCompanyRef = ref(null);
watch(
  () => userAuthStore.user?.agencyId,
  async (agencyId) => {
    if (!agencyId) {
      agencyCompanyRef.value = null;
      return;
    }
    try {
      agencyCompanyRef.value = await companyService.getCompany(agencyId);
    } catch (error) {
      logger.warn("[App] Could not load agency company for context bar", {
        agencyId,
        error: error.message,
      });
      agencyCompanyRef.value = null;
    }
  },
  { immediate: true },
);

// True only when an agency user is inside one of their CLIENT companies —
// not their own workspace (/accounts/:agencyId) and not the agency portal.
const agencyName = computed(
  () => agencyCompanyRef.value?.name || 'your agency',
);

const showAgencyContextBar = computed(() => {
  const agencyId = userAuthStore.user?.agencyId;
  if (!agencyId || viewModeStore.isImpersonating) return false;
  // Any company account, including the agency's OWN. The agency portal is home
  // base the way Nexus is for staff, so being in a company portal at all is the
  // thing worth flagging — and the way back needs to be one click from anywhere.
  return route.path.startsWith("/accounts/");
});

// The agency's own account is still "a company" here, but saying "as <agency>"
// when the company IS the agency reads as nonsense.
const isOwnAgencyCompany = computed(
  () => route.params.id === userAuthStore.user?.agencyId,
);

// Vysual staff standing in an agency's portal. Not impersonation — the guard
// admits them on usertype alone — so the only cue that they left Nexus is this
// bar. Excludes an impersonating admin: that banner owns the same grid area.
const showStaffAgencyBar = computed(
  () =>
    isAgencyPortal.value &&
    !viewModeStore.isImpersonating &&
    isAdminUser(userAuthStore.user),
);

// Admin companies are already loaded for staff on boot, so name the agency from
// that list rather than fetching the doc again.
const viewedAgencyName = computed(
  () =>
    adminCompaniesStore.companies.find((c) => c.id === route.params.agencyId)
      ?.name || 'this agency',
);

// Watch for client context changes to update body class
watch(
  isClientContext,
  (isClient) => {
    if (isClient) {
      document.body.classList.add("is-client-context");
    } else {
      document.body.classList.remove("is-client-context");
    }
  },
  { immediate: true }
);

// Watch for company changes to update theme
// For client users and impersonating admins, watch clientCompaniesStore directly
// (activeStores resolves to adminCompaniesStore for non-impersonating admins, which has no currentCompany)
watch(
  () => {
    // The agency portal is Vysual-branded on purpose: it manages OTHER companies,
    // so wearing the agency's own colors there would read as "you are inside that
    // company" — the exact confusion the separate route prefix exists to avoid.
    if (isAgencyPortal.value) return null;

    const isClientUser = userAuthStore.user?.type === 'user';
    if (isClientUser || viewModeStore.isImpersonating) {
      return clientCompaniesStore.currentCompany;
    }
    return null;
  },
  (newCompany) => {
    updateTheme(newCompany);
  },
  { immediate: true }
);

// Watch for impersonation state changes
watch(
  () => viewModeStore.isImpersonating,
  (isImpersonating) => {
    if (!isImpersonating) {
      // Reset theme when impersonation ends
      updateTheme(null);
    }
  }
);
</script>

<template>
  <!-- Dev mode indicator bar -->
  <div v-if="isDev" class="dev-banner">
    <span class="dev-banner-icon">
      <i class="fa-solid fa-code"></i>
    </span>
    LOCAL DEV
  </div>

  <!-- Impersonation indicator bar -->
  <div v-if="viewModeStore.isImpersonating" class="impersonation-banner">
    <span class="impersonation-banner-text">
      <i class="fa-solid fa-eye"></i>
      Viewing as <strong>{{ companyRef?.name || 'Client' }}</strong>
    </span>
    <span class="impersonation-banner-actions">
      <button class="impersonation-banner-exit" @click="handleBannerExit">
        <i class="fa-solid fa-arrow-left"></i>
        Back to Nexus
      </button>
      <button class="impersonation-banner-exit" title="Switch company (⌥C)" @click="openCompanySwitcherGlobal">
        <i class="fa-solid fa-arrow-right-arrow-left"></i>
        Switch company
      </button>
    </span>
  </div>

  <!-- Agency context bar: an agency user working inside a CLIENT company.
       Deliberately not impersonation — the agency user holds real membership and
       real claims, so viewModeStore is untouched. This is orientation only, so a
       staffer can't lose track of which company they're acting in. -->
  <div v-if="showAgencyContextBar" class="impersonation-banner is-agency-banner">
    <span class="impersonation-banner-text">
      <i class="fa-solid fa-building-user"></i>
      Working in <strong>{{ companyRef?.name || 'a company' }}</strong>
      <template v-if="!isOwnAgencyCompany">
        as <strong>{{ agencyName }}</strong>
      </template>
    </span>
    <router-link
      class="impersonation-banner-exit"
      :to="{ name: 'agency-companies', params: { agencyId: userAuthStore.user.agencyId } }"
    >
      <i class="fa-solid fa-arrow-left"></i>
      Back to Agency
    </router-link>
  </div>

  <!-- Staff inside an agency portal. Vysual admins reach /agency/:id by plain
       navigation (from a company's Agency tab), never impersonation — so this is
       orientation plus a way out, and the exit is a link, not a state teardown. -->
  <div v-if="showStaffAgencyBar" class="impersonation-banner is-staff-agency-banner">
    <span class="impersonation-banner-text">
      <i class="fa-solid fa-building-user"></i>
      You are viewing <strong>{{ viewedAgencyName }}</strong>
    </span>
    <router-link class="impersonation-banner-exit" :to="{ name: 'admin-companies' }">
      <i class="fa-solid fa-arrow-left"></i>
      Back to Nexus
    </router-link>
  </div>

  <!-- Only render navbar when authentication loading is complete and not on login page -->
  <NavBar v-if="showNavbar" @navbar-mounted="handleNavbarMounted" />

  <!-- Show loading spinner during auth initialization -->
  <!-- The one loader. It covers auth init AND whatever the current page is
       loading: a person waiting doesn't care which, and swapping components
       mid-wait made it flicker and change its message. -->
  <LoadingSpinner v-if="isAppLoading" fullscreen title="Loading..." />

  <!-- Main application content -->
  <main
    :class="{ 
      'no-padding': !showNavbar, 
      'has-mobile-bottom-nav': showNavbar,
      'agency-portal': isAgencyPortal
    }"
  >
    <ToastProvider>
      <router-view />
    </ToastProvider>
  </main>

  <!-- Minimized Deliverables Toolbar - Only show on admin routes -->
  <MinimizedDeliverablesToolbar
    v-if="showMinimizedDeliverablesToolbar"
    @restore="handleRestoreDeliverable"
  />

  <!-- Global Deliverable Modal -->
  <DeliverableModal
    v-if="adminDeliverablesStore.modalState.isOpen"
    :key="adminDeliverablesStore.modalState.deliverable?.id || 'new'"
    v-model="adminDeliverablesStore.modalState.isOpen"
    :deliverable="adminDeliverablesStore.modalState.deliverable"
    :project="adminDeliverablesStore.modalState.project"
    :company="adminDeliverablesStore.modalState.company"
    @update:modelValue="handleDeliverableModalClose"
    @saved="handleDeliverableModalClose"
    @deleted="handleDeliverableModalClose"
  />

  <!-- Conversations widget (client portal) -->
  <ConversationWidget
    v-if="conversationsCompanyId"
    :key="conversationsCompanyId"
    :company-id="conversationsCompanyId"
  />

  <!-- Loading Overlay -->
  <div v-if="isTransitioning" class="loading-overlay">
    <LoadingSpinner
      title="Returning to Nexus"
      subtitle="Please wait while we clean up..."
      containerClass="has-text-primary"
    />
  </div>

  <!-- Global Loading Spinner -->
  <LoadingSpinner
    v-if="globalLoadingStore.isLoading"
    :title="globalLoadingStore.loadingTitle"
    :subtitle="globalLoadingStore.loadingSubtitle"
    :min-display-time="globalLoadingStore.minDisplayTime"
    :show="globalLoadingStore.isLoading"
    :fullscreen="true"
    containerClass="has-text-primary"
  />
</template>

<style lang="scss">
html,
body {
  overflow: hidden;
}

/* Agency portal: a deliberately quieter, cooler surface than the company
   portals. The company side is white and wears the company's brand color; this
   area is Vysual-branded and manages OTHER companies, so it needs to read as a
   different place at a glance — without becoming a second design language.
   A tinted ground plus a hairline rule under the navbar is enough. */
main.agency-portal {
  /* The dark navbar now provides the separation, so no hairline rule here —
     the light ground reads as content beneath a dark chrome. */
  background-color: #f7f8fa;
  min-height: 100%;

  /* InfoBars pick up the same dark tone as the navbar. Driving it through the
     existing --info-bar-* vars rather than new classes means every InfoBar in
     the portal follows automatically. */
  --info-bar-bg: #1a2239;
  --info-bar-color: #ffffff;
  --info-bar-title-color: #ffffff;
}

#app {
  display: grid;
  grid-template-columns: 1fr;
  grid-template-rows: auto auto auto 1fr;
  gap: 0px 0px;
  grid-template-areas:
    "devbanner"
    "impersonation"
    "nav"
    "main";
  // 100vh on iOS Safari is the *largest* viewport (URL bar collapsed),
  // so when the URL bar is visible the bottom of #app extends behind
  // Safari's chrome and the mobile bottom nav vanishes off the visible
  // viewport. 100dvh measures the *current* viewport (chrome included),
  // so #app shrinks/grows as Safari's bars come and go and the nav
  // stays visible. Fallback to 100vh for browsers that don't support
  // dvh (older iOS, older Android Chrome).
  height: 100vh;
  height: 100dvh;
  overflow: hidden;
}

.dev-banner {
  grid-area: devbanner;
  background: #ff6b00;
  color: #fff;
  text-align: center;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 1.5px;
  padding: 3px 0;
  text-transform: uppercase;
  z-index: 10001;
  user-select: none;

  .dev-banner-icon {
    margin-right: 6px;
    font-size: 10px;
  }
}

/* Agency staff working inside one of their companies. Same bar as the nexus
   impersonation strip, but in the agency chrome's dark tone rather than the
   impersonation red — this is legitimate work on an account they belong to,
   not a borrowed identity, so an alarm color would be the wrong signal. */
/* Staff viewing an agency. Amber rather than the agency navy directly below it
   — a bar the same color as the nav reads as part of the nav, not a warning. */
.impersonation-banner.is-staff-agency-banner {
  background: hsl(38, 92%, 52%);
  color: hsl(30, 60%, 14%);

  .impersonation-banner-exit {
    background: rgba(0, 0, 0, 0.12);
    border-color: rgba(0, 0, 0, 0.3);
    color: hsl(30, 60%, 14%);

    &:hover {
      background: rgba(0, 0, 0, 0.22);
    }
  }
}

.impersonation-banner.is-agency-banner {
  background: linear-gradient(115deg, #1d2742 0%, #141b31 100%);

  .impersonation-banner-exit {
    border-color: rgba(255, 255, 255, 0.35);
  }
}

.impersonation-banner {
  grid-area: impersonation;
  background: hsl(348, 86%, 61%);
  color: #fff;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  padding: 4px 12px;
  z-index: 10001;
  user-select: none;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;

  .impersonation-banner-text {
    i {
      margin-right: 6px;
      font-size: 11px;
    }
  }

  // Split button: the two actions share one outline, divided by a hairline.
  .impersonation-banner-actions {
    display: inline-flex;

    .impersonation-banner-exit + .impersonation-banner-exit {
      margin-left: -1px;
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    .impersonation-banner-exit:first-child:not(:last-child) {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }
  }

  .impersonation-banner-exit {
    background: rgba(255, 255, 255, 0.2);
    border: 1px solid rgba(255, 255, 255, 0.4);
    color: #fff;
    font-size: 11px;
    font-weight: 600;
    padding: 1px 10px;
    border-radius: 3px;
    cursor: pointer;
    transition: background 0.15s ease;

    i {
      margin-right: 4px;
      font-size: 10px;
    }

    &:hover {
      background: rgba(255, 255, 255, 0.35);
    }
  }
}

.navbar {
  grid-area: nav;
}


main {
  grid-area: main;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;

  &.no-padding {
    padding: 0;
  }

  &.has-mobile-bottom-nav {
    // Account for mobile bottom nav on small screens. The nav itself is
    // 60px min-height items + 16px inner padding + safe-area padding =
    // ~76px + safe-area on a notched phone. Add a generous buffer so
    // tall items (some icons + labels can extend the rendered height
    // past 76px) and a 1rem bottom breathing space mean the last
    // scrolled-to row sits comfortably above the nav rather than
    // touching its top edge.
    @media screen and (max-width: 768px) {
      padding-bottom: calc(110px + env(safe-area-inset-bottom, 0px));
    }
  }
}

.loading-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;
}

// Client context styling
.is-client-context {
  // Any global styles that should apply when in client context
}
</style>